"use client";

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from "react";
import {
  ServiceTypeSetting,
  PaymentModeSetting,
  CustomerCategorySetting,
  CustomerTypeSetting,
} from "@/types";
import {
  serviceTypeService,
  paymentModeService,
  customerCategoryService,
  customerTypeService,
} from "@/services/settingsService";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

interface SettingsContextType {
  serviceTypes: ServiceTypeSetting[];
  paymentModes: PaymentModeSetting[];
  customerCategories: CustomerCategorySetting[];
  customerTypes: CustomerTypeSetting[];
  loading: boolean;
  error: string | null;
  
  // ServiceType operations
  addServiceType: (
    serviceType: Omit<ServiceTypeSetting, "id" | "createdAt">
  ) => ServiceTypeSetting;
  updateServiceType: (
    id: string,
    serviceType: Partial<ServiceTypeSetting>
  ) => void;
  deleteServiceType: (id: string) => void;
  
  // PaymentMode operations
  addPaymentMode: (
    paymentMode: Omit<PaymentModeSetting, "id" | "createdAt">
  ) => PaymentModeSetting;
  updatePaymentMode: (
    id: string,
    paymentMode: Partial<PaymentModeSetting>
  ) => void;
  deletePaymentMode: (id: string) => void;
  
  // CustomerCategory operations
  addCustomerCategory: (
    category: Omit<CustomerCategorySetting, "id" | "createdAt">
  ) => CustomerCategorySetting;
  updateCustomerCategory: (
    id: string,
    category: Partial<CustomerCategorySetting>
  ) => void;
  deleteCustomerCategory: (id: string) => void;
  
  // CustomerType operations
  addCustomerType: (
    type: Omit<CustomerTypeSetting, "id" | "createdAt">
  ) => CustomerTypeSetting;
  updateCustomerType: (id: string, type: Partial<CustomerTypeSetting>) => void;
  deleteCustomerType: (id: string) => void;
  
  // Fetch operations
  fetchSettings: () => Promise<void>;
  refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeSetting[]>([]);
  const [paymentModes, setPaymentModes] = useState<PaymentModeSetting[]>([]);
  const [customerCategories, setCustomerCategories] = useState<CustomerCategorySetting[]>([]);
  const [customerTypes, setCustomerTypes] = useState<CustomerTypeSetting[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const fetchSettings = useCallback(async () => {
    // Only fetch if authenticated
    if (!isAuthenticated) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Fetch service types from API (keeping this as-is)
      // const serviceTypesData = await serviceTypeService.getAll();
      
      // Load payment modes, customer categories, and customer types from mock data
      const [paymentModesData, customerCategoriesData, customerTypesData] = await Promise.all([
        paymentModeService.getAll(),
        customerCategoryService.getAll(),
        customerTypeService.getAll(),
      ]);
      
      // setServiceTypes(serviceTypesData);
      setPaymentModes(paymentModesData);
      setCustomerCategories(customerCategoriesData);
      setCustomerTypes(customerTypesData);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch settings";
      setError(errorMessage);
      console.error("Error fetching settings:", error);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const refreshSettings = useCallback(async () => {
    await fetchSettings();
  }, [fetchSettings]);

  // ServiceType operations
  const addServiceType = useCallback(
    (data: Omit<ServiceTypeSetting, "id" | "createdAt">) => {
      let newItem: ServiceTypeSetting;
      setServiceTypes((prev) => {
        newItem = {
          ...data,
          id: `ST${String(prev.length + 1).padStart(3, "0")}`,
          createdAt: new Date(),
        };
        return [newItem, ...prev];
      });
      return newItem!;
    },
    []
  );

  const updateServiceType = useCallback(
    (id: string, data: Partial<ServiceTypeSetting>) => {
      setServiceTypes((prev) =>
        prev.map((s) => (s.id === id ? { ...s, ...data } : s))
      );
    },
    []
  );

  const deleteServiceType = useCallback((id: string) => {
    setServiceTypes((prev) => prev.filter((s) => s.id !== id));
  }, []);

  // PaymentMode operations
  const addPaymentMode = useCallback(
    (data: Omit<PaymentModeSetting, "id" | "createdAt">) => {
      let newItem: PaymentModeSetting;
      setPaymentModes((prev) => {
        newItem = {
          ...data,
          id: `PM${String(prev.length + 1).padStart(3, "0")}`,
          createdAt: new Date(),
        };
        return [newItem, ...prev];
      });
      return newItem!;
    },
    []
  );

  const updatePaymentMode = useCallback(
    (id: string, data: Partial<PaymentModeSetting>) => {
      setPaymentModes((prev) =>
        prev.map((p) => (p.id === id ? { ...p, ...data } : p))
      );
    },
    []
  );

  const deletePaymentMode = useCallback((id: string) => {
    setPaymentModes((prev) => prev.filter((p) => p.id !== id));
  }, []);

  // CustomerCategory operations
  const addCustomerCategory = useCallback(
    (data: Omit<CustomerCategorySetting, "id" | "createdAt">) => {
      let newItem: CustomerCategorySetting;
      setCustomerCategories((prev) => {
        newItem = {
          ...data,
          id: `CC${String(prev.length + 1).padStart(3, "0")}`,
          createdAt: new Date(),
        };
        return [newItem, ...prev];
      });
      return newItem!;
    },
    []
  );

  const updateCustomerCategory = useCallback(
    (id: string, data: Partial<CustomerCategorySetting>) => {
      setCustomerCategories((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...data } : c))
      );
    },
    []
  );

  const deleteCustomerCategory = useCallback((id: string) => {
    setCustomerCategories((prev) => prev.filter((c) => c.id !== id));
  }, []);

  // CustomerType operations
  const addCustomerType = useCallback(
    (data: Omit<CustomerTypeSetting, "id" | "createdAt">) => {
      let newItem: CustomerTypeSetting;
      setCustomerTypes((prev) => {
        newItem = {
          ...data,
          id: `CT${String(prev.length + 1).padStart(3, "0")}`,
          createdAt: new Date(),
        };
        return [newItem, ...prev];
      });
      return newItem!;
    },
    []
  );

  const updateCustomerType = useCallback(
    (id: string, data: Partial<CustomerTypeSetting>) => {
      setCustomerTypes((prev) =>
        prev.map((t) => (t.id === id ? { ...t, ...data } : t))
      );
    },
    []
  );

  const deleteCustomerType = useCallback((id: string) => {
    setCustomerTypes((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Fetch settings on mount, but only if authenticated
  useEffect(() => {
    // Wait for auth to finish loading, then check if authenticated
    if (!authLoading && isAuthenticated) {
      fetchSettings();
    }
  }, [fetchSettings, authLoading, isAuthenticated]);

  const value: SettingsContextType = {
    serviceTypes,
    paymentModes,
    customerCategories,
    customerTypes,
    loading,
    error,
    addServiceType,
    updateServiceType,
    deleteServiceType,
    addPaymentMode,
    updatePaymentMode,
    deletePaymentMode,
    addCustomerCategory,
    updateCustomerCategory,
    deleteCustomerCategory,
    addCustomerType,
    updateCustomerType,
    deleteCustomerType,
    fetchSettings,
    refreshSettings,
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}

